import { Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RtoNote } from '@/types/order';
import { formatDateByDayjs } from '@/utils';
import { cn } from '@/lib/utils';

interface RtoNoteCardProps {
  note: RtoNote;
  /** One line for a list row; the full card on the order page. */
  compact?: boolean;
}

/**
 * BOXY-376 — the return request note: what the merchant (or the Boxy team)
 * wrote, who and when, and the carrier's refusal when there was one.
 */
export function RtoNoteCard({ note, compact = false }: RtoNoteCardProps) {
  const { t } = useTranslation();
  const refused = note.status === 'refused';

  const title = refused
    ? String(t('rto_note_refused_title'))
    : note.requested_by?.type === 'boxy'
      ? String(t('rto_note_title_boxy'))
      : String(t('rto_note_title_merchant'));

  const role =
    note.requested_by?.type === 'boxy'
      ? String(t('rto_note_role_boxy'))
      : String(t('rto_note_role_merchant'));
  const meta = [
    note.requested_at ? formatDateByDayjs(note.requested_at, 'D MMM HH:mm') : null,
    note.requested_by?.name ? `${note.requested_by.name} (${role})` : role,
  ]
    .filter(Boolean)
    .join(' · ');

  return compact ? (
    <span
      data-testid='rto-note-inline'
      title={note.note ?? undefined}
      className={cn(
        'block max-w-[220px] truncate text-[11px]',
        refused ? 'text-sd-destructive' : 'text-sd-muted-foreground',
      )}
    >
      {note.note ? `“${note.note}”` : title}
    </span>
  ) : (
    <section
      aria-label={title}
      className={cn(
        'mb-4 flex items-start gap-3 rounded-2xl border border-solid px-4 py-3.5',
        refused ? 'border-sd-destructive/30 bg-sd-destructive/5' : 'border-sd-border bg-sd-card',
      )}
    >
      <Undo2
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          refused ? 'text-sd-destructive' : 'text-sd-muted-foreground',
        )}
      />
      <div className='min-w-0 flex-1'>
        <p className='m-0 text-[13px] font-bold text-sd-foreground'>{title}</p>
        {note.note ? (
          <p className='m-0 mt-1 break-words text-[13px] text-sd-foreground'>“{note.note}”</p>
        ) : null}
        {refused && note.reason ? (
          <p className='m-0 mt-1 text-[12px] text-sd-destructive'>{note.reason}</p>
        ) : null}
        <p className='m-0 mt-1 text-[12px] text-sd-muted-foreground'>{meta}</p>
      </div>
    </section>
  );
}

export default RtoNoteCard;
